import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/tabs"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui/typography"
import { cn } from "@/ui/utils"

type Provider = "cloudflare" | "vercel" | "aws"

interface Row {
  label: string
  cloudflare: string
  vercel: string
  aws: string
}

const providers: { key: Provider; label: string }[] = [
  { key: "cloudflare", label: "🟠 Cloudflare" },
  { key: "vercel", label: "▲ Vercel" },
  { key: "aws", label: "☁️ AWS" },
]

const freeTierRows: Row[] = [
  {
    label: "Requests",
    cloudflare: "3M/mo",
    vercel: "1M/mo",
    aws: "Lambda: 1M/mo",
  },
  {
    label: "Database",
    cloudflare: "D1: 5 GB, 5M reads/day",
    vercel: "Neon: 0.5 GB",
    aws: "RDS: 20 GB (12-mo trial)",
  },
  {
    label: "KV Storage",
    cloudflare: "1 GB, 100K reads/day",
    vercel: "Upstash: 256 MB, 16K cmd/day",
    aws: "DynamoDB: 25 GB",
  },
  {
    label: "Object Storage",
    cloudflare: "R2: 10 GB",
    vercel: "Blob: 1 GB",
    aws: "S3: 5 GB",
  },
  {
    label: "Egress",
    cloudflare: "$0",
    vercel: "$0 (up to 100 GB)",
    aws: "$0 (up to 100 GB)",
  },
]

const paidTierRows: Row[] = [
  {
    label: "Base Price",
    cloudflare: "$5/mo",
    vercel: "$20/user/mo",
    aws: "Pay-as-you-go",
  },
  {
    label: "Requests",
    cloudflare: "10M/mo + $0.30/M",
    vercel: "10M/mo + $2/M",
    aws: "$0.20/M requests",
  },
  {
    label: "Database",
    cloudflare: "D1: 5GB + $0.75/GB",
    vercel: "Neon: $0.35/GB",
    aws: "RDS: ~$13/mo + $0.115/GB",
  },
  {
    label: "KV Storage",
    cloudflare: "$0.50/M reads, $5/M writes",
    vercel: "Upstash: $2/M commands",
    aws: "$0.25/M reads, $1.25/M writes",
  },
  {
    label: "Object Storage",
    cloudflare: "R2: $0.015/GB/mo",
    vercel: "Blob: $0.023/GB/mo",
    aws: "S3: $0.023/GB/mo",
  },
  {
    label: "Egress",
    cloudflare: "$0",
    vercel: "1 TB/mo + $0.15/GB",
    aws: "1 TB/mo + $0.09/GB",
  },
]

function ComparisonTable({ rows }: { rows: Row[] }) {
  return (
    <Table className="my-0">
      <TableHeader>
        <TableRow>
          <TableHead />
          {providers.map((p) => (
            <TableHead
              key={p.key}
              align="center"
              className={cn(p.key === "cloudflare" && "bg-primary/5")}
            >
              {p.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>

      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.label}>
            <TableCell>{row.label}</TableCell>
            {providers.map((p) => (
              <TableCell
                key={p.key}
                align="center"
                className={cn(p.key === "cloudflare" && "bg-primary/5 text-foreground")}
              >
                {row[p.key]}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export function PricingComparison() {
  return (
    <div className="mx-auto max-w-5xl">
      <Tabs defaultValue="free">
        <div className="mb-6 flex justify-center">
          <TabsList className="h-11 p-1">
            <TabsTrigger value="free" className="px-6 text-base">
              Free Tier
            </TabsTrigger>
            <TabsTrigger value="paid" className="px-6 text-base">
              Paid Tier
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="free">
          <ComparisonTable rows={freeTierRows} />
        </TabsContent>

        <TabsContent value="paid">
          <ComparisonTable rows={paidTierRows} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
